/**
 * @todo Check if:
 * - [ ] "Delete user" needs unique property which isn't `status`?
 */

//#region Imports
import {
  IAnydoAuth,
  IAnydoElectronPreload,
  AuthStatusCode,
  PremiumSubscriptionPaymentProvider,
  PromiseStatus,
} from "@anydo/interfaces";
import { logError, AnydoAuthError, isAuthStatusCodeError, isUserDetailsPremiumUser } from "@anydo/core";
import type { IAnalyticsContextValue } from "@anydo/analytics-react";
import type { IAuthCaptchaRequestDto } from "@anydo/auth";
import { AuthReactAdapterExectutionBlockQueue } from "./AuthReactAdapterExectutionBlockQueue";
import { AuthReactAdapterState, ReadonlyAuthReactAdapterStateStateT } from "./AuthReactAdapterState";

declare var AnydoElectronPreload: void | IAnydoElectronPreload;
//#endregion

//#region Config
const IS_ELECTRON = typeof AnydoElectronPreload !== "undefined";
//#endregion

export class AuthReactAdapter {
  //#region Setup
  private readonly _auth: IAnydoAuth;
  private readonly _analytics: IAnalyticsContextValue;
  private readonly _state: AuthReactAdapterState;
  private readonly _queue = new AuthReactAdapterExectutionBlockQueue();
  private _offs: Array<() => void> | null = null;
  private _offSubscriptionListener: (() => void) | null = null;

  constructor(auth: IAnydoAuth, analytics: IAnalyticsContextValue) {
    this._auth = auth;
    this._analytics = analytics;

    this._state = new AuthReactAdapterState(getInitialState(this._auth));
  }

  /**
   * Subscription allowed only once as only single `AuthReactAdapter` should be
   * used for single `AuthProvider`
   */
  subscribe(listener: () => void) {
    // No re subscribe
    if (this._offSubscriptionListener !== null) {
      throw new Error("AuthReactAdapter#subscribe(): Only single subscription allowed");
    }

    this._offs = [
      // Execution queue
      this._queue.onError((err) => logError(err, "AuthReactAdapter#_queue#onError()")),
      this._queue.onQueueFinish(this._handleQueueFinshed),

      // Auth
      this._auth.subscribe("loggedIn", this._handleAuthStatusChange),
      this._auth.subscribe("loggedOut", this._handleAuthStatusChange),
      this._auth.subscribe("subscriptionChange", this._handleAuthStatusChange),
      this._auth.subscribe("userDetailsUpdate", this._handleAuthStatusChange),
    ];

    // Since `use-subscription` check updates immediately after subscribing,
    // we're making sure no state is lost until subscription happens (until effect
    // batch), without trigger effect while subscribing
    this._state.updateImmediate(getAuthComputedState(this._auth));

    // The actual subscription
    this._offSubscriptionListener = this._state.onStateChange(listener);

    return () => {
      // eslint-disable-next-line no-unused-expressions
      this._offSubscriptionListener?.();
      this._offSubscriptionListener = null;

      if (this._offs === null) return;
      for (let off of this._offs) off();
      this._offs = null;
    };
  }

  destroy() {
    this._queue.destroy();
    (this._queue as AuthReactAdapter["_queue"]) = undefined!;

    this._state.destroy();
    (this._state as AuthReactAdapter["_state"]) = undefined!;

    // eslint-disable-next-line no-unused-expressions
    this._offSubscriptionListener?.();
    this._offSubscriptionListener = null;

    if (this._offs !== null) {
      for (let off of this._offs) off();
      this._offs = null;
    }
  }
  //#endregion

  //#region private Event
  private _handleQueueFinshed = () => {
    this._state.flushUpdates();
  };

  private _handleAuthStatusChange = () => {
    const canFlush = this._state.isEmpty() && !this._queue.isPending();
    this._state.pushUpdate(getAuthComputedState(this._auth));

    if (canFlush) {
      this._state.flushUpdates();
    }
  };
  //#endregion

  //#region Private - actions
  private _runAuthOpIfQueueEmpty(op: () => Promise<void>) {
    if (this._queue.isPending()) return;

    this._queue.addOpIfEmpty(async () => {
      this._state.updateImmediate({ isPending: true });

      const { status, isLoggedIn } = this._state.getState();

      try {
        await op();
      } catch (error) {
        if (AnydoAuthError.isAnydoAuthError(error)) {
          this._state.pushUpdate({
            status: (error as AnydoAuthError).code,
          });
        } else {
          logError(error, "AuthReactAdapter#_runAuthOp()");
        }
      }

      this._state.pushUpdate((state) => {
        /**
         * If status change during `op` we use the new status. But there in
         * "success operation" `status` isn't changed, so there are some cases
         * that we need to make "smart" assumptions to update the status, such
         * as:
         * - delete user
         * - user logged in
         * - user logged out
         */
        let newStatus = status;

        if (state.status !== status) {
          newStatus = state.status;
        } else if (isLoggedIn && !state.isLoggedIn) {
          newStatus = AuthStatusCode.NONE;
        } else if (
          (state.status === AuthStatusCode.NONE || state.status === AuthStatusCode.TWO_FACTOR_REQUIRED) &&
          state.isLoggedIn
        ) {
          newStatus = AuthStatusCode.LOGGED_IN;
        }

        return {
          ...getAuthComputedState(this._auth),
          status: newStatus,
          isStatusError: isAuthStatusCodeError(state.status),
          isPending: false,
        };
      });
    });
  }
  //#endregion

  //#region Public - State
  getState() {
    return this._state.getState();
  }

  waitNextState() {
    return new Promise<void | undefined>((resolve) => {
      this._state.onceStateChange(() => resolve());
    });
  }
  //#endregion

  //#region Public - Login actions
  join(userName: string, email: string, password: string, captchaPayload?: IAuthCaptchaRequestDto) {
    this._runAuthOpIfQueueEmpty(async () => {
      await this._auth.join(userName, email, password, captchaPayload);

      if (this._auth.isNewUser()) {
        this._analytics.track("registered", { extra_str1: "email" });
      } else {
        this._analytics.track("logged_in", { extra_str1: "email" });
      }
    });
  }

  loginWithEmail(email: string, password: string): void {
    this._runAuthOpIfQueueEmpty(async () => {
      await this._auth.login(email, password);
      this._analytics.track("logged_in", { extra_str1: "email" });
    });
  }
  loginWithEmail2fa(email: string, password: string, code: string): void {
    this._runAuthOpIfQueueEmpty(async () => {
      await this._auth.login2fa(email, password, code);
      this._analytics.track("logged_in", { extra_str1: "email" });
    });
  }

  preLoginWithEmail2fa(email: string, password: string): void {
    this._runAuthOpIfQueueEmpty(async () => {
      await this._auth.preLoginWithEmail2fa(email, password);
      this._state.pushUpdate({ status: AuthStatusCode.TWO_FACTOR_REQUIRED });
    });
  }

  loginWithGoogle() {
    this._runAuthOpIfQueueEmpty(async () => {
      await this._auth.loginWithGoogle();

      if (this._auth.isNewUser()) {
        this._analytics.track("registered", { extra_str1: "google" });
      } else {
        this._analytics.track("logged_in", { extra_str1: "google" });
      }
    });
  }

  loginWithFacebook() {
    this._runAuthOpIfQueueEmpty(async () => {
      if (IS_ELECTRON) {
        await this._auth.loginWithFacebook();

        if (this._auth.isNewUser()) {
          this._analytics.track("registered", { extra_str1: "facebook" });
        } else {
          this._analytics.track("logged_in", { extra_str1: "facebook" });
        }
      } else {
        this._auth.redirectLoginWithFacebook();
      }
    });
  }

  loginWithApple() {
    this._runAuthOpIfQueueEmpty(async () => {
      // Electron (Works only in MAS build currently because sandbox issues with `electron-builder`)
      if (IS_ELECTRON) {
        await this._auth.loginWithApple();

        if (this._auth.isNewUser()) {
          this._analytics.track("registered", { extra_str1: "apple" });
        } else {
          this._analytics.track("logged_in", { extra_str1: "apple" });
        }
      }

      // Browser
      else {
        await this._auth.redirectLoginWithApple();

        // If it doesn't fail, we cannot check when the user actually redirected, so
        // we make sure the state will stuck at "loading"
        await Promise.race([]);
      }
    });
  }

  async logout() {
    this._runAuthOpIfQueueEmpty(async () => {
      this._analytics.track("user_signed_out");

      await this._auth.logout();
    });
  }

  sendPremiumUserStrpieInvoices() {
    const { isPostPremiumStripeInvoicesVisible, postPremiumStripeInvoicesStatus } = this._state.getState();

    if (!isPostPremiumStripeInvoicesVisible) return;
    if (postPremiumStripeInvoicesStatus === PromiseStatus.PENDING) return;
    if (postPremiumStripeInvoicesStatus === PromiseStatus.RESOLVED) return;
    this._runAuthOpIfQueueEmpty(async () => {
      const { isPostPremiumStripeInvoicesVisible, postPremiumStripeInvoicesStatus } = this._state.getState();
      if (!isPostPremiumStripeInvoicesVisible) return;
      if (postPremiumStripeInvoicesStatus === PromiseStatus.PENDING) return;
      if (postPremiumStripeInvoicesStatus === PromiseStatus.RESOLVED) return;

      this._state.updateImmediate({
        postPremiumStripeInvoicesStatus: PromiseStatus.PENDING,
      });

      try {
        await this._auth.postPremiumStripeInvoices();

        this._state.pushUpdate({
          postPremiumStripeInvoicesStatus: PromiseStatus.RESOLVED,
        });
      } catch {
        this._state.pushUpdate({
          postPremiumStripeInvoicesStatus: PromiseStatus.REJECTED,
        });
      }
    });
  }
  //#endregion

  //#region Public - utilities action
  resetStatusError() {
    // We needs to run this regardless there is an "op" pending
    this._queue.addOp(async () => {
      this._state.pushUpdate((state) => {
        return {
          status: state.isLoggedIn ? AuthStatusCode.LOGGED_IN : AuthStatusCode.NONE,
          isStatusError: false,
        };
      });
    });
  }
  //#endregion

  //#region Public - Auth misc
  deleteUser(email: string, password: string) {
    this._runAuthOpIfQueueEmpty(async () => {
      await this._auth.deleteUser(email, password);
    });
  }
  //#endregion

  //#region Public - Onboarding
  completeOnboarding() {
    this._runAuthOpIfQueueEmpty(async () => {
      try {
        await this._auth.completeOnboarding();

        this._state.pushUpdate({
          userDetails: this._auth.getUserDetails(),
        });
      } catch (error) {
        logError(error, "AuthReactAdapter#completeOnboarding()");
      }
    });
  }
  //#endregion
}

//#region Helpers
function getAuthComputedState(auth: IAnydoAuth) {
  const userDetails = auth.getUserDetails();
  const userSubscription = auth.getUserSubscription();
  const isPremiumUser = isUserDetailsPremiumUser(userDetails, userSubscription);

  const isPostPremiumStripeInvoicesVisible: boolean =
    isPremiumUser && userSubscription?.paymentProvider === PremiumSubscriptionPaymentProvider.STRIPE;

  return {
    isLoggedIn: auth.isLoggedIn(),
    isNewUser: auth.isNewUser(),
    isPremiumUser,
    userDetails,
    userSubscription,
    isPostPremiumStripeInvoicesVisible,
  };
}

function getInitialState(auth: IAnydoAuth): ReadonlyAuthReactAdapterStateStateT {
  const authComputedState = getAuthComputedState(auth);
  const status = authComputedState.isLoggedIn ? AuthStatusCode.LOGGED_IN : AuthStatusCode.NONE;

  return {
    ...authComputedState,
    // Blocking
    status,
    isPending: false,
    isStatusError: isAuthStatusCodeError(status),
    //
    postPremiumStripeInvoicesStatus: PromiseStatus.NONE,
  };
}

// function waitNextWindowFocus() {
//   return new Promise<void>((resolve) => {
//     function handleFocus() {
//       window.removeEventListener("focus", handleFocus);
//       resolve();
//     }

//     window.addEventListener("focus", handleFocus);
//   });
// }
//#endregion
